import React, { useState } from 'react';
import { insertPrediction } from '../../Config/matudb';
import { Upload, FileSpreadsheet, CheckCircle, XCircle, AlertCircle, Download, Loader, Info } from 'lucide-react';
import * as XLSX from 'xlsx';

const BulkPronosticsUpload = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState([]);
  const [preview, setPreview] = useState([]);

  // Función para generar archivo Excel de plantilla
  const downloadTemplate = () => {
    const templateData = [
      {
        'Equipo Local': 'Real Madrid',
        'Logo Local': 'https://example.com/realmadrid.png',
        'Equipo Visitante': 'Barcelona',
        'Logo Visitante': 'https://example.com/barcelona.png',
        'Confianza (%)': 75,
        'Cuota': 1.85,
        'Pronóstico': '1',
        'Fecha (DD/MM/YYYY)': '21/10/2025',
        'Hora (HH:MM)': '15:30',
        'Estado': 'pendiente',
        'Es VIP (SI/NO)': 'NO',
        'Precio VIP': 0
      },
      {
        'Equipo Local': 'Manchester United',
        'Logo Local': 'https://example.com/manutd.png',
        'Equipo Visitante': 'Liverpool',
        'Logo Visitante': 'https://example.com/liverpool.png',
        'Confianza (%)': 80,
        'Cuota': 2.10,
        'Pronóstico': 'Over 2.5',
        'Fecha (DD/MM/YYYY)': '22/10/2025',
        'Hora (HH:MM)': '18:00',
        'Estado': 'pendiente',
        'Es VIP (SI/NO)': 'SI',
        'Precio VIP': 5.00
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Pronósticos');
    
    // Ajustar ancho de columnas
    ws['!cols'] = [
      { wch: 20 }, // Equipo Local
      { wch: 40 }, // Logo Local
      { wch: 20 }, // Equipo Visitante
      { wch: 40 }, // Logo Visitante
      { wch: 15 }, // Confianza
      { wch: 10 }, // Cuota
      { wch: 20 }, // Pronóstico
      { wch: 20 }, // Fecha
      { wch: 15 }, // Hora
      { wch: 15 }, // Estado
      { wch: 15 }, // Es VIP
      { wch: 12 }  // Precio VIP
    ];

    XLSX.writeFile(wb, 'plantilla_pronosticos.xlsx');
  };

  // Validar una fila de pronóstico
  const validateRow = (row, rowIndex) => {
    const errors = [];
    
    if (!row['Equipo Local']) errors.push(`Fila ${rowIndex + 2}: Falta Equipo Local`);
    if (!row['Equipo Visitante']) errors.push(`Fila ${rowIndex + 2}: Falta Equipo Visitante`);
    if (!row['Logo Local']) errors.push(`Fila ${rowIndex + 2}: Falta Logo Local`);
    if (!row['Logo Visitante']) errors.push(`Fila ${rowIndex + 2}: Falta Logo Visitante`);
    if (!row['Confianza (%)'] || row['Confianza (%)'] < 1 || row['Confianza (%)'] > 100) {
      errors.push(`Fila ${rowIndex + 2}: Confianza debe estar entre 1 y 100`);
    }
    if (!row['Cuota'] || row['Cuota'] < 1) {
      errors.push(`Fila ${rowIndex + 2}: Cuota debe ser mayor a 1`);
    }
    if (!row['Pronóstico']) errors.push(`Fila ${rowIndex + 2}: Falta Pronóstico`);
    if (!row['Fecha (DD/MM/YYYY)']) errors.push(`Fila ${rowIndex + 2}: Falta Fecha`);
    if (!row['Hora (HH:MM)']) errors.push(`Fila ${rowIndex + 2}: Falta Hora`);
    
    // Validar formato de fecha
    const datePattern = /^\d{2}\/\d{2}\/\d{4}$/;
    if (row['Fecha (DD/MM/YYYY)'] && !datePattern.test(row['Fecha (DD/MM/YYYY)'])) {
      errors.push(`Fila ${rowIndex + 2}: Formato de fecha incorrecto (debe ser DD/MM/YYYY)`);
    }
    
    // Validar formato de hora
    const timePattern = /^\d{2}:\d{2}$/;
    if (row['Hora (HH:MM)'] && !timePattern.test(row['Hora (HH:MM)'])) {
      errors.push(`Fila ${rowIndex + 2}: Formato de hora incorrecto (debe ser HH:MM)`);
    }
    
    // Validar estado
    const validStates = ['pendiente', 'ganada', 'perdida', 'nula'];
    if (row['Estado'] && !validStates.includes(row['Estado'].toLowerCase())) {
      errors.push(`Fila ${rowIndex + 2}: Estado debe ser pendiente, ganada, perdida o nula`);
    }

    return errors;
  };

  // Convertir fila de Excel a formato de pronóstico
  const rowToPronostico = (row) => {
    const isVIP = row['Es VIP (SI/NO)']?.toString().toUpperCase() === 'SI';
    
    return {
      homeTeam: {
        name: row['Equipo Local'].toString().trim(),
        logo: row['Logo Local'].toString().trim()
      },
      awayTeam: {
        name: row['Equipo Visitante'].toString().trim(),
        logo: row['Logo Visitante'].toString().trim()
      },
      confidence: parseInt(row['Confianza (%)']),
      odds: parseFloat(row['Cuota']),
      prediction: row['Pronóstico'].toString().trim(),
      date: row['Fecha (DD/MM/YYYY)'].toString().trim(),
      hours: row['Hora (HH:MM)'].toString().trim(),
      state: row['Estado'] ? row['Estado'].toString().toLowerCase() : 'pendiente',
      block: isVIP,
      value: isVIP ? parseFloat(row['Precio VIP'] || 0) : 0
    };
  };

  // Manejar selección de archivo
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResults(null);
      setErrors([]);
      setPreview([]);
      previewFile(selectedFile);
    }
  };

  // Vista previa del archivo
  const previewFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        // Mostrar solo las primeras 5 filas para preview
        setPreview(jsonData.slice(0, 5));
      } catch (error) {
        console.error('Error al leer archivo:', error);
        setErrors(['Error al leer el archivo Excel']);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Procesar y subir pronósticos
  const handleUpload = async () => {
    if (!file) {
      setErrors(['Por favor selecciona un archivo']);
      return;
    }

    setLoading(true);
    setErrors([]);
    setResults(null);

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          setErrors(['El archivo está vacío']);
          setLoading(false);
          return;
        }

        // Validar todas las filas
        let allErrors = [];
        jsonData.forEach((row, index) => {
          const rowErrors = validateRow(row, index);
          allErrors = [...allErrors, ...rowErrors];
        });

        if (allErrors.length > 0) {
          setErrors(allErrors);
          setLoading(false);
          return;
        }

        let successCount = 0;
        let failCount = 0;
        const uploadErrors = [];

        for (let i = 0; i < jsonData.length; i++) {
          try {
            const pronostico = rowToPronostico(jsonData[i]);
            const table = pronostico.block ? 'abetvip' : 'abet';
            await insertPrediction(table, pronostico);
            successCount++;
          } catch (error) {
            failCount++;
            uploadErrors.push(`Fila ${i + 2}: ${error.message}`);
          }
        }

        setResults({
          total: jsonData.length,
          success: successCount,
          failed: failCount
        });

        if (uploadErrors.length > 0) {
          setErrors(uploadErrors);
        }

        // Limpiar archivo después de subir exitosamente
        if (successCount > 0) {
          setFile(null);
          setPreview([]);
        }

      } catch (error) {
        console.error('Error al procesar archivo:', error);
        setErrors([`Error al procesar archivo: ${error.message}`]);
      } finally {
        setLoading(false);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-white">Carga Masiva de Pronósticos</h2>
          <p className="text-white/60">Sube múltiples pronósticos desde un archivo Excel</p>
        </div>
        <button
          onClick={downloadTemplate}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
        >
          <Download size={20} />
          <span>Descargar Plantilla</span>
        </button>
      </div>

      {/* Instrucciones */}
      <div className="bg-blue-900/20 backdrop-blur-lg rounded-lg p-4 border border-blue-700">
        <div className="flex items-start space-x-3">
          <Info className="text-blue-400 flex-shrink-0 mt-1" size={20} />
          <div className="text-white/80 text-sm space-y-2">
            <p className="font-semibold text-white">Instrucciones:</p>
            <ol className="list-decimal list-inside space-y-1 ml-2">
              <li>Descarga la plantilla de Excel haciendo clic en el botón verde</li>
              <li>Completa el archivo con los datos de tus pronósticos</li>
              <li>Asegúrate de seguir el formato exacto de las columnas</li>
              <li>Sube el archivo completado usando el botón de abajo</li>
              <li>Revisa la vista previa antes de confirmar la carga</li>
            </ol>
            <p className="mt-2 text-yellow-400">
              <strong>Importante:</strong> El estado debe ser "pendiente", "ganada", "perdida" o "nula". 
              Para pronósticos VIP, marca "SI" en la columna "Es VIP" y define el precio.
            </p>
          </div>
        </div>
      </div>

      {/* Área de carga de archivo */}
      <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 border border-white/20">
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className="w-full max-w-md">
            <label htmlFor="file-upload" className="cursor-pointer">
              <div className="border-2 border-dashed border-white/40 rounded-lg p-8 hover:border-blue-500 hover:bg-white/5 transition-all duration-300 text-center">
                {file ? (
                  <div className="space-y-2">
                    <FileSpreadsheet className="mx-auto text-green-400" size={48} />
                    <p className="text-white font-medium">{file.name}</p>
                    <p className="text-white/60 text-sm">
                      {(file.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="mx-auto text-white/60" size={48} />
                    <p className="text-white font-medium">
                      Haz clic para seleccionar un archivo Excel
                    </p>
                    <p className="text-white/60 text-sm">
                      o arrastra y suelta aquí
                    </p>
                  </div>
                )}
              </div>
            </label>
            <input
              id="file-upload"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          {file && (
            <button
              onClick={handleUpload}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg flex items-center space-x-2 transition-colors font-medium"
            >
              {loading ? (
                <>
                  <Loader className="animate-spin" size={20} />
                  <span>Procesando...</span>
                </>
              ) : (
                <>
                  <Upload size={20} />
                  <span>Subir Pronósticos</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Vista previa */}
      {preview.length > 0 && (
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-4 border border-white/20">
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
            <FileSpreadsheet className="mr-2" size={20} />
            Vista Previa (primeras {preview.length} filas)
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-white/60 border-b border-white/20">
                  <th className="p-2">Local</th>
                  <th className="p-2">Visitante</th>
                  <th className="p-2">Pronóstico</th>
                  <th className="p-2">Cuota</th>
                  <th className="p-2">Conf.</th>
                  <th className="p-2">Fecha</th>
                  <th className="p-2">Hora</th>
                  <th className="p-2">VIP</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((row, index) => (
                  <tr key={index} className="text-white border-b border-white/10">
                    <td className="p-2">{row['Equipo Local']}</td>
                    <td className="p-2">{row['Equipo Visitante']}</td>
                    <td className="p-2">{row['Pronóstico']}</td>
                    <td className="p-2">{row['Cuota']}</td>
                    <td className="p-2">{row['Confianza (%)']}%</td>
                    <td className="p-2">{row['Fecha (DD/MM/YYYY)']}</td>
                    <td className="p-2">{row['Hora (HH:MM)']}</td>
                    <td className="p-2">
                      {row['Es VIP (SI/NO)']?.toString().toUpperCase() === 'SI' ? (
                        <span className="text-yellow-400">SI (${row['Precio VIP']})</span>
                      ) : (
                        <span className="text-gray-400">NO</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Resultados */}
      {results && (
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 border border-white/20">
          <h3 className="text-lg font-semibold text-white mb-4">Resultados de la Carga</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-900/30 rounded-lg p-4 border border-blue-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm">Total</p>
                  <p className="text-2xl font-bold text-white">{results.total}</p>
                </div>
                <FileSpreadsheet className="text-blue-400" size={32} />
              </div>
            </div>
            <div className="bg-green-900/30 rounded-lg p-4 border border-green-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm">Exitosos</p>
                  <p className="text-2xl font-bold text-green-400">{results.success}</p>
                </div>
                <CheckCircle className="text-green-400" size={32} />
              </div>
            </div>
            <div className="bg-red-900/30 rounded-lg p-4 border border-red-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm">Fallidos</p>
                  <p className="text-2xl font-bold text-red-400">{results.failed}</p>
                </div>
                <XCircle className="text-red-400" size={32} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Errores */}
      {errors.length > 0 && (
        <div className="bg-red-900/20 backdrop-blur-lg rounded-lg p-4 border border-red-700">
          <h3 className="text-lg font-semibold text-red-400 mb-3 flex items-center">
            <AlertCircle className="mr-2" size={20} />
            Errores Encontrados ({errors.length})
          </h3>
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {errors.map((error, index) => (
              <div key={index} className="text-red-300 text-sm flex items-start">
                <span className="mr-2">•</span>
                <span>{error}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkPronosticsUpload;

